import type {
    Stages,
    MobileAlgoContext,
    ConversionDal,
    ConversionAlgoConfig,
    AppliedHeuristicLogMessage,
    IStageRunner,
    Predicate,
    StageRuntimeStore
} from '../types'
import type {SnapshotDal} from '@wix/document-manager-core'
import {StageName, SkipPredicate} from '../enums'
import _ from 'lodash'

const stageOrder: StageName[] = [
    StageName.HIDE_COMPONENTS,
    StageName.GROUPING,
    StageName.GROUPING_ORDER,
    StageName.STRUCTURE_ORDER,
    StageName.HORIZONTAL_LAYOUT_SCALE,
    StageName.VERTICAL_LAYOUT_SCALE,
    StageName.TRANSFORM_STYLE,
    StageName.POST_PROCESS
]

const initSubscriptions = () => ({
    [StageName.HIDE_COMPONENTS]: [],
    [StageName.GROUPING]: [],
    [StageName.GROUPING_ORDER]: [SkipPredicate.GROUPING_HASH],
    [StageName.STRUCTURE_ORDER]: [SkipPredicate.GROUPING_HASH],
    [StageName.HORIZONTAL_LAYOUT_SCALE]: [SkipPredicate.GROUPING_HASH],
    [StageName.VERTICAL_LAYOUT_SCALE]: [SkipPredicate.GROUPING_HASH],
    [StageName.TRANSFORM_STYLE]: [SkipPredicate.GROUPING_HASH],
    [StageName.POST_PROCESS]: [SkipPredicate.GROUPING_HASH]
})

export class StageRunner implements IStageRunner {
    private predicates: Partial<Record<SkipPredicate, Predicate>> = {}
    private subscriptions: Record<StageName, SkipPredicate[]> = initSubscriptions()

    private constructor(private stages: Stages) {}

    static create(stages: Stages) {
        return new StageRunner(stages)
    }

    private async registerSkipPredicate(key: SkipPredicate, predicate: Predicate) {
        this.predicates[key] = predicate
    }

    private subscribeToSkipPredicate(stageName: StageName, key: SkipPredicate) {
        this.subscriptions[stageName].push(key)
    }

    private getRuntimeStore(stageName: StageName): StageRuntimeStore {
        return {
            registerSkipPredicate: this.registerSkipPredicate.bind(this),
            subscribeToSkipPredicate: _.partial(this.subscribeToSkipPredicate.bind(this), stageName)
        }
    }

    private flush() {
        this.predicates = {}
        this.subscriptions = initSubscriptions()
    }

    private shouldSkipStage(stageName: StageName): boolean {
        const skipPredicates = this.subscriptions[stageName]
        return skipPredicates.some(key => {
            try {
                return this.predicates[key]?.() ?? false
            } catch (error) {
                console.warn(`Skip predicate "${key}" threw an error:`, error)
                return false
            }
        })
    }

    async run(
        ctx: MobileAlgoContext,
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        pageId: string,
        config: ConversionAlgoConfig,
        logger?: AppliedHeuristicLogMessage[]
    ) {
        for (const stageName of stageOrder) {
            if (this.shouldSkipStage(stageName)) {
                continue
            }

            const stage = this.stages[stageName]
            await stage.run(
                ctx,
                conversionDal,
                initialSnapshot,
                pageId,
                config,
                this.getRuntimeStore(stageName),
                logger
            )
            conversionDal.commitChanges(stageName)
        }

        this.flush()
    }
}
